import {
  DrawerBody,
  DrawerHeader,
  FormController,
  FormLabel,
  Input,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AddInstoreReceipt } from '../../../../api/oms/order-history';
import { inStoreErrors } from '../../../../constants/receipt-error';
import { useCommonContext } from '../../../../hooks/useContexts';
import {
  IFormData,
  IInStoreReceiptError,
  IReceiptForm,
} from '../../../../models/purchase-history/add-receipt';
import {
  addHyphens,
  addSlash,
  makeInStoreRequestBody,
} from '../../../../utils/order-history';
import { AddReceiptDrawerFooter } from './AddReceiptDrawerFooter';

export const InStoreReceiptForm = ({
  closeDrawer,
  setAlertState,
  setDrawerStepper,
  svocId,
}: IReceiptForm) => {
  const { userID } = useThdCustomer();
  const [inputError, setInputError] = useState({
    receiptNumber: '',
    salesDate: '',
    preTaxAmount: '',
  });
  const [isLoading, setLoading] = useState(false);

  const { isMobile } = useCommonContext();

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      preTaxAmount: '',
      receiptNumber: '',
      salesDate: '',
      tryAgain404: false,
      apiError: false,
    },
  });
  const watchReceiptNumber = watch('receiptNumber');
  const watchSalesDate = watch('salesDate');
  const watchPreTaxAmount = watch('preTaxAmount');

  useEffect(() => {
    const invalidChars = /[$&+,:;=\\?@(){}~%\][|/'<>.^*()%!]/;
    if (invalidChars?.test(watchReceiptNumber)) {
      setInputError((prevState) => ({
        ...prevState,
        receiptNumber: 'error',
      }));
      setError('receiptNumber', { type: 'invalid', message: 'invalid entry' });
      return;
    }
    if (watchReceiptNumber?.length === 16) {
      setInputError((prevState) => ({
        ...prevState,
        receiptNumber: 'success',
      }));
      clearErrors('receiptNumber');
    }
    if (
      inputError?.receiptNumber === 'success' &&
      watchReceiptNumber?.length !== 16
    ) {
      setInputError((prevState) => ({
        ...prevState,
        receiptNumber: 'error',
      }));
      setError('receiptNumber', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchReceiptNumber]);

  useEffect(() => {
    if (watchSalesDate?.length === 8) {
      const tempYear = watchSalesDate?.split('/');
      const tempDate = new Date(
        `${tempYear[2] < '20' ? '19' : '20'}${tempYear[2]}/${tempYear[0]}/${
          tempYear[1]
        }`,
      );
      if (Number.isNaN(tempDate.getTime())) {
        setInputError((prevState) => ({
          ...prevState,
          salesDate: 'error',
        }));
        setError('salesDate', { type: 'invalid', message: 'invalid entry' });
        return;
      }
      setInputError((prevState) => ({
        ...prevState,
        salesDate: 'success',
      }));
      clearErrors('salesDate');
      return;
    }
    if (inputError?.salesDate === 'success' && watchSalesDate?.length !== 8) {
      setInputError((prevState) => ({
        ...prevState,
        salesDate: 'error',
      }));
      setError('salesDate', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchSalesDate]);

  useEffect(() => {
    if (watchPreTaxAmount?.length > 3 && watchPreTaxAmount?.at(-3) === '.') {
      setInputError((prevState) => ({
        ...prevState,
        preTaxAmount: 'success',
      }));
      clearErrors('preTaxAmount');
    } else if (
      watchPreTaxAmount?.length > 1 &&
      watchPreTaxAmount?.at(-3) !== '.'
    ) {
      setInputError((prevState) => ({
        ...prevState,
        preTaxAmount: 'error',
      }));
    }
    if (
      inputError?.preTaxAmount === 'success' &&
      (watchPreTaxAmount?.length < 3 || watchPreTaxAmount?.at(-3) !== '.')
    ) {
      setInputError((prevState) => ({
        ...prevState,
        preTaxAmount: 'error',
      }));
      setError('preTaxAmount', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchPreTaxAmount]);

  const onFormSubmit: SubmitHandler<IFormData> = async ({
    preTaxAmount,
    receiptNumber,
    salesDate,
  }) => {
    clearErrors();
    try {
      let request;
      if (preTaxAmount && receiptNumber && salesDate && svocId) {
        setLoading(true);

        request = makeInStoreRequestBody(
          svocId,
          receiptNumber,
          salesDate,
          preTaxAmount,
        );

        const response = await AddInstoreReceipt(request, userID);
        const status = response ? response.status : null;

        if (status === null) {
          setError('tryAgain404', {
            type: 'failed',
            message: 'failed request',
          });
          return;
        }
        closeDrawer();
        reset();
        setAlertState({
          alertMessage: `You've successfully added an in-store receipt to your account. View Receipt ${receiptNumber}`,
          status: 'success',
        });
      }
    } catch (error: unknown) {
      const errorDescription: string | undefined = (
        error as IInStoreReceiptError
      ).response?.data?.error?.[0]?.description;

      if ((error as IInStoreReceiptError).response.status === 500) {
        setError('tryAgain404', {
          type: 'failed',
          message: 'failed request',
        });
        return;
      }
      let errorMessage;
      try {
        errorMessage = JSON.parse(errorDescription as string)
          ?.addReceiptResponse?.receipts?.[0]?.errors?.[0]?.description;
      } catch (err) {
        errorMessage =
          'We were unable to retrieve your receipt details. Refresh the page and try again.';
      }
      setError('apiError', {
        type: 'failed',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    clearErrors();
    handleSubmit(onFormSubmit)();
  };

  return (
    <div
      data-testid="instoreReceiptForm"
      className="sui-flex sui-flex-col sui-h-full"
    >
      <DrawerHeader
        title="Add In-Store Receipt"
        onClose={closeDrawer}
        onBack={() => setDrawerStepper && setDrawerStepper(0)}
      />
      <DrawerBody
        style={{
          height: isMobile ? '65vh' : '',
        }}
      >
        <Typography variant="body-base">
          Please enter the following details found on your receipt:
        </Typography>
        <div className="sui-mt-2 sui-flex sui-justify-center">
          <img
            src="https://assets.thdstatic.com/images/v1/ReceiptImage.svg"
            alt="Receipt Example"
            width={isMobile ? '200px' : '320px'}
            height={isMobile ? '180px' : '210px'}
          />
        </div>
        <FormController
          className="sui-mt-[8px] md:sui-mt-6"
          fullWidth
          status={
            inputError.receiptNumber || (errors?.receiptNumber && 'error')
          }
        >
          <FormLabel htmlFor="receiptNumber">
            Receipt Number<sup>*</sup>
          </FormLabel>
          <Input
            aria-describedby="receiptNumber-input"
            fullWidth
            id="receiptNumber"
            inputProps={{
              maxLength: 16,
            }}
            {...register('receiptNumber', {
              required: true,
              minLength: 15,
              onChange: (event: { target: { value: string } }) =>
                setValue(
                  'receiptNumber',
                  addHyphens(event.target.value.toUpperCase()),
                ),
              onBlur: (event: { target: { value: string } }) =>
                setValue(
                  'receiptNumber',
                  addHyphens(event.target.value.toUpperCase()),
                ),
            })}
            placeholder="XXXX-XXXXX-XXXXX"
          />
          {(inputError.receiptNumber === 'error' || errors.receiptNumber) && (
            <Typography variant="body-xs" color="danger">
              {errors?.receiptNumber?.type === 'tryAgain' &&
                inStoreErrors.receiptNumberNotFound}
              {(['required', 'minLength', 'invalid', 'pattern'].includes(
                errors?.receiptNumber?.type ?? 'invalid',
              ) ||
                inputError.receiptNumber === 'error') &&
                inStoreErrors.receiptNumberInvalidFormat}
            </Typography>
          )}
        </FormController>
        <FormController
          className="sui-mt-[8px] md:sui-mt-6"
          fullWidth
          status={inputError.salesDate || (errors?.salesDate && 'error')}
        >
          <FormLabel htmlFor="salesDate">
            Purchase Date<sup>*</sup>
          </FormLabel>
          <Input
            aria-describedby="salesDate-input"
            fullWidth
            id="salesDate"
            inputProps={{
              maxLength: 8,
            }}
            {...register('salesDate', {
              required: true,
              minLength: 7,
              pattern: /^(\d{2})\/(\d{2})\/(\d{2})$/,
              onChange: (event: { target: { value: string } }) =>
                setValue('salesDate', addSlash(event.target.value)),
              onBlur: (event: { target: { value: string } }) =>
                setValue('salesDate', addSlash(event.target.value)),
            })}
            placeholder="MM/DD/YY"
          />
          {(inputError.salesDate === 'error' || errors.salesDate) && (
            <Typography variant="body-xs" color="danger">
              {errors?.salesDate?.type === 'tryAgain' &&
                inStoreErrors.salesDateNotFound}
              {(['required', 'minLength', 'invalid', 'pattern'].includes(
                errors?.salesDate?.type ?? 'invalid',
              ) ||
                inputError.salesDate === 'error') &&
                inStoreErrors.salesDateInvalidFormat}
            </Typography>
          )}
        </FormController>
        <FormController
          className="sui-mt-[8px] md:sui-mt-6"
          fullWidth
          status={inputError.preTaxAmount || (errors?.preTaxAmount && 'error')}
        >
          <FormLabel htmlFor="preTaxAmount">
            Pre-Tax Amount<sup>*</sup>
          </FormLabel>
          <Input
            aria-describedby="preTaxAmount-input"
            fullWidth
            id="preTaxAmount"
            {...register('preTaxAmount', {
              required: true,
              minLength: 3,
              onChange: (event: { target: { value: string } }) =>
                setValue('preTaxAmount', event.target.value),
              onBlur: (event: { target: { value: string } }) =>
                setValue('preTaxAmount', event.target.value),
            })}
            placeholder="$"
          />
          {(inputError.preTaxAmount === 'error' || errors.preTaxAmount) && (
            <Typography variant="body-xs" color="danger">
              {inStoreErrors.preTaxAmountInvalidFormat}
            </Typography>
          )}
        </FormController>
        {errors.tryAgain404 && (
          <div className="sui-mt-6">
            <Typography color="danger">{inStoreErrors.api404Error}</Typography>
          </div>
        )}
        {errors.apiError && (
          <div className="sui-mt-6">
            <Typography color="danger">{errors?.apiError?.message}</Typography>
          </div>
        )}
        <AddReceiptDrawerFooter
          closeDrawer={closeDrawer}
          handleButtonClick={handleButtonClick}
          isLoading={isLoading}
          primaryButtonText="Add In-Store Receipt"
          reset={reset}
          secondaryButtonText="Cancel"
        />
      </DrawerBody>
    </div>
  );
};
